/**
 * useChronicleAutoPowerOff — set Remote auto power-off timer (HAR-392).
 *
 * Mirrors `useChronicleUsbMode`: optimistic FE update → Tauri IPC → confirm /
 * rollback. Device value is pushed via `chronicle:state_changed` after
 * post-connect `APP&GET&SHUTDOWN` and after a successful set.
 */
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useRef, useState } from 'react';

import {
  chronicleConnectionContextAtom,
  chronicleDeviceStateAtom,
} from '@/atoms/chronicle';
import { chronicleBridge } from '@/tauri/chronicle';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';

export function useChronicleAutoPowerOff() {
  const [isUpdating, setIsUpdating] = useState(false);
  const isUpdatingRef = useRef(false);
  const setDeviceState = useSetAtom(chronicleDeviceStateAtom);
  const deviceState = useAtomValue(chronicleDeviceStateAtom);
  const connectionContext = useAtomValue(chronicleConnectionContextAtom);

  const setAutoPowerOff = useCallback(
    async (minutes: number) => {
      if (isUpdatingRef.current) return;
      isUpdatingRef.current = true;
      setIsUpdating(true);

      // Snapshot before the optimistic write — keep the Jotai updater pure
      // (same pattern as deriving USB rollback from the toggle argument).
      const previousMinutes = deviceState.autoPowerOffMinutes;
      setDeviceState((prev) => ({ ...prev, autoPowerOffMinutes: minutes }));

      const deviceId = connectionContext.linkedDeviceId ?? '';

      try {
        const confirmedValue = await chronicleBridge.setAutoPowerOff(minutes);
        setDeviceState((prev) => ({
          ...prev,
          autoPowerOffMinutes: confirmedValue ?? minutes,
        }));
        logger.info({
          msg: '[useChronicleAutoPowerOff] Auto power-off updated',
          minutes: confirmedValue ?? minutes,
          deviceId,
        });
      } catch (error) {
        setDeviceState((prev) => ({
          ...prev,
          autoPowerOffMinutes: previousMinutes,
        }));
        logger.error({
          msg: '[useChronicleAutoPowerOff] Failed to set auto power-off',
          error: error instanceof Error ? error.message : String(error),
          deviceId,
        });
        throw error;
      } finally {
        isUpdatingRef.current = false;
        setIsUpdating(false);
      }
    },
    [setDeviceState, deviceState.autoPowerOffMinutes, connectionContext.linkedDeviceId]
  );

  return { setAutoPowerOff, isUpdating };
}
